import { FullscreenOverlayContainer, OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import player from 'lottie-web';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { provideLottieOptions } from 'ngx-lottie';
import { MarkdownModule, MARKED_OPTIONS } from 'ngx-markdown';
import { NgxMaskModule } from 'ngx-mask';
import { MatGoogleMapsAutocompleteModule } from 'src/lib/google-maps-autocomplete/module/mat-google-maps-autocomplete.module';

import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { initializer } from './app/auth/auth-init';
import { msalInstanceFactory, msalInterceptorConfigFactory } from './app/auth/msal-factories';
import { AdminAlertsModule } from './app/core/components/admin-alerts/admin-alerts.module';
import { AdminAlertsEffects } from './app/core/components/admin-alerts/store/effects';
import { adminAlertsReducer } from './app/core/components/admin-alerts/store/reducers';
import { currencyMaskConfig } from './app/core/constants/currency-mask-config';
import { MockResponseInterceptor } from './app/core/interceptors/mock-response/mock-response.interceptor';
import { NetworkInterceptor } from './app/core/interceptors/network.interceptor';
import { AppAnalyticsHydratorService } from './app/core/services/app-analytics-hydrator.service';
import { CustomizableBannerService } from './app/core/services/customizable-banner.service';
import { DatadogErrorHandler } from './app/core/services/datadog-error-handler.service';
import { ModalTrackingService } from './app/core/services/modal-tracking.service';
import { PostHogService } from './app/core/services/posthog.service';
import { SnackbarService } from './app/core/services/snackbar.service';
import { SourceResourceService } from './app/core/services/source-resource.service';
import { UserService } from './app/core/services/user.service';
import { customizableBannerReducer } from './app/core/store/reducers/customizable-banner.reducer';
import { featureFlagsReducer } from './app/core/store/reducers/feature-flags.reducer';
import { savedFilterReducer } from './app/core/store/reducers/saved-filter.reducer';
import { supplierCategoryReducer } from './app/core/store/reducers/supplier-category.reducer';
import { userReducer } from './app/core/store/reducers/user.reducer';
import { environment } from './environments/environment';
import { AgmCoreModule } from './lib/google-maps-autocomplete/module/amg-core.module';
import { addGoogleTagManagerLoadingScript } from './lib/google-tag-manager';
import { addHubspotLoadingScript } from './lib/hubspot';
import { NgxCurrencyModule } from './lib/ngx-currency/src/currency-mask.module';

const additionalInterceptorsList = environment.production
  ? []
  : [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MockResponseInterceptor,
        multi: true,
      },
    ];

if (environment.production) {
  addGoogleTagManagerLoadingScript();
  addHubspotLoadingScript();
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    MatSnackBar,
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      MatDatepickerModule,
      MatDialogModule,
      MatMomentDateModule,
      MatTooltipModule,
      NgxCurrencyModule.forRoot(currencyMaskConfig),
      NgxDaterangepickerMd.forRoot(),
      AgmCoreModule.forRoot({
        apiKey: 'AIzaSyCYNC2mVvulJindhiA9tgC7hSsY3ZPsJ_0',
        libraries: ['places'],
        language: 'EN',
      }),
      MatGoogleMapsAutocompleteModule.forRoot(),
      AdminAlertsModule,
      OverlayModule,
      MarkdownModule.forRoot({
        markedOptions: {
          provide: MARKED_OPTIONS,
          useValue: { breaks: true },
        },
      }),
      StoreModule.forRoot({
        user: userReducer,
        customizableBanners: customizableBannerReducer,
        supplierCategories: supplierCategoryReducer,
        adminAlerts: adminAlertsReducer,
        savedFilters: savedFilterReducer,
        featureFlags: featureFlagsReducer,
      }),
      EffectsModule.forRoot([AdminAlertsEffects]),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
        connectInZone: true,
      }),
      NgxMaskModule.forRoot(),
    ),
    provideLottieOptions({ player: () => player }),
    provideAppInitializer(() => {
      const initializerFn = initializer(
        inject(UserService),
        inject(SnackbarService),
        inject(CustomizableBannerService),
        inject(PostHogService),
        inject(SourceResourceService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => inject(AppAnalyticsHydratorService).start()),
    provideAppInitializer(() => inject(ModalTrackingService).start()),
    ...additionalInterceptorsList,
    {
      provide: ErrorHandler,
      useClass: DatadogErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetworkInterceptor,
      multi: true,
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        autoFocus: false,
        disableClose: true,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
      deps: [MsalBroadcastService],
    },
    {
      provide: OverlayContainer,
      useClass: FullscreenOverlayContainer,
    },
    MsalService,
    MsalBroadcastService,
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
